/**
 * Bundle 163 - Ops Triage Tile for incomplete signups.
 *
 * Rendered at the top of /admin/incidents. Fetches the live
 * count from
 * `GET /api/admin/incidents/tiles/incomplete-signups`.
 *
 * Clicking the tile deep-links to /admin/users/incomplete-
 * profiles where the same rows are actionable. Zero-typing
 * ops principle: admin should not have to remember or paste
 * an email.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { AlertTriangle, Users } from 'lucide-react';
import api from '@/utils/api';

const Stat = ({ label, value, tone, testId }) => (
  <div className="flex flex-col" data-testid={testId}>
    <span className={`text-2xl font-semibold ${tone}`}>{value}</span>
    <span className="text-[10px] uppercase tracking-widest text-neutral-500">
      {label}
    </span>
  </div>
);

const IncompleteSignupsTile = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data: body } = await api.get(
          '/admin/incidents/tiles/incomplete-signups');
        if (mounted) setData(body);
      } catch (e) {
        if (mounted) {
          setError(
            e?.response?.data?.detail
            || 'Failed to load tile.');
        }
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const total = data?.total ?? 0;
  const leak = data?.leak ?? 0;
  const flagged = data?.flagged ?? 0;
  const legacy = data?.legacy ?? 0;

  return (
    <Link
      to="/admin/users/incomplete-profiles"
      className="block group"
      data-testid="incomplete-signups-tile-link"
    >
      <Card
        className={
          'border shadow-soft transition-all hover:shadow-md '
          + (leak > 0
            ? 'border-red-200 hover:border-red-300'
            : 'border-neutral-200 hover:border-primary-300')
        }
        data-testid="incomplete-signups-tile"
      >
        <CardContent className="p-4">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <Users className="h-4 w-4 text-primary-800" />
                <div className="text-[10px] uppercase tracking-widest text-secondary-700">
                  Incomplete signups
                </div>
                {leak > 0 ? (
                  <span
                    className="inline-flex items-center gap-1 rounded-full border border-red-200 bg-red-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-red-800"
                    data-testid="incomplete-signups-tile-leak-badge"
                  >
                    <AlertTriangle className="h-3 w-3" />
                    {leak} leak{leak === 1 ? '' : 's'}
                  </span>
                ) : null}
              </div>
              <div className="text-sm text-primary-900 font-semibold mb-2">
                {loading ? 'Loading.' : error
                  ? 'Could not load'
                  : 'Open the triage list'}
              </div>
              <div className="grid grid-cols-4 gap-6 pt-1">
                <Stat
                  label="Total"
                  value={total}
                  tone="text-primary-900"
                  testId="incomplete-signups-tile-total"
                />
                <Stat
                  label="Leak"
                  value={leak}
                  tone={leak > 0 ? 'text-red-700' : 'text-neutral-500'}
                  testId="incomplete-signups-tile-leak"
                />
                <Stat
                  label="Flagged"
                  value={flagged}
                  tone={flagged > 0 ? 'text-amber-700' : 'text-neutral-500'}
                  testId="incomplete-signups-tile-flagged"
                />
                <Stat
                  label="Legacy"
                  value={legacy}
                  tone="text-neutral-600"
                  testId="incomplete-signups-tile-legacy"
                />
              </div>
              {error ? (
                <div className="text-xs text-red-700 mt-2">{error}</div>
              ) : null}
            </div>
            <div className="text-xs text-secondary-700 group-hover:text-primary-900 whitespace-nowrap">
              Open list →
            </div>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
};

export default IncompleteSignupsTile;
